import type { Maintainer } from '../../types/maintainer';
import type { SheetMaintainer } from './crag-sheet-pdf';

/**
 * The maintainers' logos as PNG data URLs, which is all react-pdf can draw:
 * it reads neither SVG nor WebP, and a club's logo is often one of the two.
 * The browser can read them all, so each is drawn onto a canvas and written
 * back out as PNG — at its own proportions, from the original rather than the
 * square thumbnail, which crops a wide logo to its middle.
 *
 * The file is fetched rather than handed to an <img> by its address, because
 * Active Storage serves an SVG as `application/octet-stream` with `nosniff`
 * (an SVG can carry script, so it is never served as one) and an image
 * element refuses it. Fetched, it is given its real type back and drawn from
 * a blob URL — which, being this page's own, also leaves the canvas readable.
 * Inside an <img> an SVG runs no script, so this is safe to do.
 *
 * A logo that cannot be read (gone, blocked, not an image) leaves the club's
 * name in its place: the footer must never come out with a hole in it.
 */

/** Tall enough to stay sharp at the footer's size once printed at 300 dpi. */
const LOGO_PIXELS_HIGH = 180;
const LOGO_TIMEOUT_MS = 10_000;

type Logo = SheetMaintainer['logo'];

async function svgAware(blob: Blob): Promise<{ blob: Blob; vector: boolean }> {
  if (blob.type === 'image/svg+xml') return { blob, vector: true };
  if (blob.type.startsWith('image/')) return { blob, vector: false };
  const head = (await blob.slice(0, 512).text()).trimStart();
  if (/^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*(<!DOCTYPE svg[^>]*>\s*)?<svg[\s>]/i.test(head)) {
    return { blob: new Blob([blob], { type: 'image/svg+xml' }), vector: true };
  }
  return { blob, vector: false };
}

function drawToPng(src: string, vector: boolean): Promise<Logo> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        // An SVG with only a viewBox may report no size of its own.
        const naturalWidth = img.naturalWidth || img.width || 300;
        const naturalHeight = img.naturalHeight || img.height || 150;
        // A picture is never blown up past its own pixels; a drawing has none.
        const height = vector ? LOGO_PIXELS_HIGH : Math.min(LOGO_PIXELS_HIGH, naturalHeight);
        const width = Math.max(1, Math.round((naturalWidth / naturalHeight) * height));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return resolve(null);
        context.drawImage(img, 0, 0, width, height);
        resolve({ src: canvas.toDataURL('image/png'), width, height });
      } catch {
        resolve(null);
      }
    };
    img.src = src;
  });
}

export async function loadLogo(url: string): Promise<Logo> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), LOGO_TIMEOUT_MS);
  let objectUrl: string | null = null;
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
    if (!response.ok) return null;
    const { blob, vector } = await svgAware(await response.blob());
    objectUrl = URL.createObjectURL(blob);
    return await drawToPng(objectUrl, vector);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

export async function loadSheetMaintainers(maintainers: Maintainer[]): Promise<SheetMaintainer[]> {
  return Promise.all(
    maintainers.map(async (maintainer) => {
      const donationUrl = maintainer.donation_url || null;
      // The original first, for its shape; the square thumbnail only if the
      // original cannot be read.
      const candidates = [maintainer.logo_url, maintainer.logo_small_url].filter((url): url is string => !!url);
      for (const url of candidates) {
        const logo = await loadLogo(url);
        if (logo) return { name: maintainer.name, logo, donationUrl };
      }
      return { name: maintainer.name, logo: null, donationUrl };
    })
  );
}
